import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../useAuth";
import { APIError } from "@api/types/backendTypes";

export interface CreateFacebookDatasetRequest {
  organisationId: string;
  dataset_id: string;
  capi_token: string;
}

export const useCreateFacebookDataset = () => {
  const queryClient = useQueryClient();
  const { data } = useAuth();

  return useMutation({
    mutationFn: async ({
      organisationId,
      dataset_id,
      capi_token,
    }: CreateFacebookDatasetRequest) => {
      if (!data?.token) {
        throw new Error("User Session not available");
      }
      if (!organisationId || !dataset_id || !capi_token) {
        throw new Error(
          "Organisation ID, Dataset ID, and CAPI token are required for Facebook Dataset connection"
        );
      }

      try {
        const headers: Record<string, string> = {
          Accept: "application/json",
          "Content-Type": "application/json",
          "tracify-token": data?.token ?? "",
        };

        const response = await axios.post(
          `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/facebook-datasets/${organisationId}/create`,
          {
            dataset_id,
            capi_token,
          },
          {
            headers,
          }
        );

        if (response.status !== 200) {
          throw new Error(response.data.error);
        }

        return response.data;
      } catch (err: any) {
        if (axios.isAxiosError(err)) {
          if (!err.response) {
            throw err;
          }
          const axiosError = err as AxiosError;

          // Handle specific Facebook Dataset error cases
          if (axiosError.response?.status === 404) {
            throw new Error("Dataset not found");
          }

          const apiError = axiosError.response?.data as APIError;
          const detail =
            apiError?.detail ?? apiError?.error ?? axiosError.message;
          throw new Error(detail);
        }
        throw new Error("Unknown error occurred!");
      }
    },
    onSuccess: (data, values) => {
      queryClient.invalidateQueries({ queryKey: ["datasets"] });
    },
  });
};
